import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Check,
  Eye,
  FolderOpen,
  ImagePlus,
  Loader2,
  Pencil,
  Save,
  Share2,
  Trash2,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { useAuth } from "../contexts/auth-context";
import { handleApiAuthError } from "../lib/auth-required";
import type { OpenedFile } from "../lib/file";
import { createContractFromUpload } from "../lib/platform-contracts";
import {
  getScannedDocument,
  listScannedDocumentSummaries,
  newScanDocId,
  removeScannedDocument,
  upsertScannedDocument,
  type ScannedDocSummary,
  type ScanFilterId,
  type StoredScanPage,
} from "../lib/scan-library";
import {
  clientToNormalized,
  createQuadHomography,
  getImageContentBox,
  normalizedToPixels,
  orderCorners,
  outputSizeForQuad,
  writeBilinearSample,
  type ImageContentBox,
  type NormPoint,
} from "../lib/scan-crop-math";
import { saveAndShareBytes } from "../lib/shareText";
import {
  SimpleDocSaveModal,
  type SimpleDocSaveDestination,
} from "./SimpleDocSaveModal";
import { ToolPageShell } from "./ToolPages";

type ScanPage = StoredScanPage;

type Mode = "capture" | "crop" | "view";
type Screen = "editor" | "library";

type Props = {
  onBack: () => void;
  onOpenPdf: (file: OpenedFile) => void;
  apiBase: string;
  onOpenContract?: (contractId: string, title: string) => void;
  initialDocId?: string | null;
  startInLibrary?: boolean;
};

const DEFAULT_CORNERS: [NormPoint, NormPoint, NormPoint, NormPoint] = [
  { x: 0.08, y: 0.08 },
  { x: 0.92, y: 0.08 },
  { x: 0.92, y: 0.92 },
  { x: 0.08, y: 0.92 },
];

const FILTER_OPTIONS: Array<{ id: ScanFilterId; label: string }> = [
  { id: "none", label: "بدون فیلتر" },
  { id: "document", label: "سند" },
  { id: "grayscale", label: "سیاه‌وسفید" },
  { id: "contrast", label: "کنتراست" },
  { id: "bw", label: "متن واضح" },
];

function uid(): string {
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function toPersianDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]!);
}

function formatScanDate(iso: string): string {
  try {
    return toPersianDigits(
      new Date(iso).toLocaleString("fa-IR", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    );
  } catch {
    return iso;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("بارگذاری تصویر ناموفق"));
    img.src = src;
  });
}

/**
 * Perspective warp using DLT homography,
 * sampling the natural-resolution original — never the CSS display box.
 */
async function warpPerspective(
  srcUrl: string,
  corners: [NormPoint, NormPoint, NormPoint, NormPoint],
): Promise<string> {
  const img = await loadImage(srcUrl);
  const sourceCorners = normalizedToPixels(
    corners,
    img.naturalWidth,
    img.naturalHeight,
  );
  const { outW, outH } = outputSizeForQuad(sourceCorners);
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas در دسترس نیست");

  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = img.naturalWidth;
  srcCanvas.height = img.naturalHeight;
  const sctx = srcCanvas.getContext("2d", { willReadFrequently: true });
  if (!sctx) throw new Error("canvas در دسترس نیست");
  sctx.drawImage(img, 0, 0);
  const srcData = sctx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);

  const dest = ctx.createImageData(outW, outH);
  const sw = srcCanvas.width;
  const sh = srcCanvas.height;
  const homography = createQuadHomography(sourceCorners, outW, outH);

  for (let y = 0; y < outH; y++) {
    for (let x = 0; x < outW; x++) {
      const p = homography.sampleSource(x, y);
      const di = (y * outW + x) * 4;
      writeBilinearSample(srcData.data, dest.data, di, sw, sh, p.x, p.y);
      if (dest.data[di + 3] === 0) {
        dest.data[di] = 255;
        dest.data[di + 1] = 255;
        dest.data[di + 2] = 255;
        dest.data[di + 3] = 255;
      }
    }
  }
  ctx.putImageData(dest, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.92);
}

async function applyFilter(
  srcUrl: string,
  filter: ScanFilterId,
): Promise<string> {
  if (filter === "none") return srcUrl;
  const img = await loadImage(srcUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return srcUrl;
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = data.data;

  for (let i = 0; i < d.length; i += 4) {
    const r = d[i];
    const g = d[i + 1];
    const b = d[i + 2];
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;

    if (filter === "grayscale") {
      d[i] = d[i + 1] = d[i + 2] = gray;
      continue;
    }

    if (filter === "contrast") {
      const c = (v: number) =>
        Math.min(255, Math.max(0, (v - 128) * 1.35 + 128));
      d[i] = c(r);
      d[i + 1] = c(g);
      d[i + 2] = c(b);
      continue;
    }

    if (filter === "document") {
      const lifted = Math.min(255, Math.max(0, (gray - 40) * 1.35 + 20));
      const v =
        lifted > 170 ? 255 : lifted < 90 ? Math.max(0, lifted * 0.7) : lifted;
      d[i] = d[i + 1] = d[i + 2] = v;
      continue;
    }

    // bw — soft threshold for text
    const v = gray > 145 ? 255 : gray < 110 ? Math.max(0, gray * 0.45) : gray;
    d[i] = d[i + 1] = d[i + 2] = v;
  }

  ctx.putImageData(data, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.9);
}

async function dataUrlToUint8(dataUrl: string): Promise<Uint8Array> {
  const res = await fetch(dataUrl);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

async function buildScanPdfBytes(pages: ScanPage[]): Promise<Uint8Array> {
  const ready = pages.filter((p) => p.processedUrl || p.croppedUrl);
  if (!ready.length) throw new Error("حداقل یک صفحه برش‌خورده لازم است");
  const pdf = await PDFDocument.create();
  for (const page of ready) {
    const bytes = await dataUrlToUint8(page.processedUrl || page.croppedUrl!);
    const img = await pdf.embedJpg(bytes);
    const p = pdf.addPage([img.width, img.height]);
    p.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
  }
  return pdf.save();
}

function scanFileName(title: string): string {
  const base = title.trim().replace(/\.pdf$/i, "") || "اسکن";
  return `${base}.pdf`;
}

export function ScanDocumentPage({
  onBack,
  onOpenPdf: _onOpenPdf,
  apiBase,
  onOpenContract,
  initialDocId = null,
  startInLibrary = false,
}: Props) {
  const { isAuthenticated, openLogin } = useAuth();
  const [screen, setScreen] = useState<Screen>(
    startInLibrary ? "library" : "editor",
  );
  const [library, setLibrary] = useState<ScannedDocSummary[]>([]);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryId, setLibraryId] = useState<string | null>(initialDocId);
  const [pages, setPages] = useState<ScanPage[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("capture");
  const [corners, setCorners] =
    useState<[NormPoint, NormPoint, NormPoint, NormPoint]>(DEFAULT_CORNERS);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ScanFilterId>("none");
  const [docName, setDocName] = useState(
    () => `اسکن-${new Date().toLocaleDateString("fa-IR")}`,
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const cropRef = useRef<HTMLDivElement>(null);
  const cropImgRef = useRef<HTMLImageElement>(null);
  const [imageBox, setImageBox] = useState<ImageContentBox | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const loadedInitialRef = useRef<string | null>(null);

  const active = useMemo(
    () => pages.find((p) => p.id === activeId) ?? null,
    [pages, activeId],
  );

  const isEmptyCapture = mode === "capture" && pages.length === 0;
  const hasReadyPages = pages.some((p) => p.processedUrl || p.croppedUrl);

  const refreshLibrary = useCallback(async () => {
    setLibraryBusy(true);
    try {
      setLibrary(await listScannedDocumentSummaries());
    } catch {
      setLibrary([]);
    } finally {
      setLibraryBusy(false);
    }
  }, []);

  const resetEditor = useCallback((opts?: { keepLibraryId?: boolean }) => {
    if (!opts?.keepLibraryId) setLibraryId(null);
    setPages([]);
    setActiveId(null);
    setMode("capture");
    setCorners(DEFAULT_CORNERS);
    setFilter("none");
    setDocName(`اسکن-${new Date().toLocaleDateString("fa-IR")}`);
    setError(null);
    setSaveOpen(false);
    setSaveError(null);
  }, []);

  const loadDocument = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const doc = await getScannedDocument(id);
      if (!doc) {
        setError("سند اسکن‌شده پیدا نشد");
        return;
      }
      setLibraryId(doc.id);
      setDocName(doc.title);
      setPages(doc.pages);
      setActiveId(null);
      setMode("capture");
      setCorners(DEFAULT_CORNERS);
      setFilter("none");
      setScreen("editor");
    } catch (err) {
      setError(err instanceof Error ? err.message : "بارگذاری اسکن ناموفق");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (screen === "library") void refreshLibrary();
  }, [screen, refreshLibrary]);

  useEffect(() => {
    if (!initialDocId) return;
    if (loadedInitialRef.current === initialDocId) return;
    loadedInitialRef.current = initialDocId;
    void loadDocument(initialDocId);
  }, [initialDocId, loadDocument]);

  const addFromFile = useCallback(async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("خواندن عکس ناموفق"));
        reader.readAsDataURL(file);
      });
      const id = uid();
      setPages((prev) => [
        ...prev,
        {
          id,
          originalDataUrl: dataUrl,
          croppedUrl: null,
          processedUrl: null,
          corners: DEFAULT_CORNERS,
          filter: "none",
        },
      ]);
      setActiveId(id);
      setCorners(DEFAULT_CORNERS);
      setFilter("none");
      setMode("crop");
      setScreen("editor");
    } catch (err) {
      setError(err instanceof Error ? err.message : "خطا");
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  }, []);

  async function applyCrop() {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const warped = await warpPerspective(active.originalDataUrl, corners);
      const processedUrl = await applyFilter(warped, filter);
      setPages((prev) =>
        prev.map((p) =>
          p.id === active.id
            ? {
                ...p,
                croppedUrl: warped,
                processedUrl,
                corners,
                filter,
              }
            : p,
        ),
      );
      setMode("view");
    } catch (err) {
      setError(err instanceof Error ? err.message : "برش ناموفق");
    } finally {
      setBusy(false);
    }
  }

  async function changeFilter(next: ScanFilterId) {
    setFilter(next);
    if (!active?.croppedUrl) return;
    setBusy(true);
    setError(null);
    try {
      const processedUrl = await applyFilter(active.croppedUrl, next);
      setPages((prev) =>
        prev.map((p) =>
          p.id === active.id ? { ...p, filter: next, processedUrl } : p,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "اعمال فیلتر ناموفق");
    } finally {
      setBusy(false);
    }
  }

  function updateImageBox() {
    if (!cropRef.current || !cropImgRef.current) {
      setImageBox(null);
      return;
    }
    const stageRect = cropRef.current.getBoundingClientRect();
    const content = getImageContentBox(cropImgRef.current);
    setImageBox({
      left: content.left - stageRect.left,
      top: content.top - stageRect.top,
      width: content.width,
      height: content.height,
    });
  }

  function setCornerFromPointer(clientX: number, clientY: number, idx: number) {
    if (!cropImgRef.current) return;
    const content = getImageContentBox(cropImgRef.current);
    const offset = dragOffsetRef.current;
    const point = clientToNormalized(
      clientX - (offset?.x ?? 0),
      clientY - (offset?.y ?? 0),
      content,
    );
    if (!point) return;
    setCorners((prev) => {
      const next = [...prev] as [NormPoint, NormPoint, NormPoint, NormPoint];
      next[idx] = point;
      return next;
    });
  }

  function beginDrag(e: React.PointerEvent, idx: number) {
    e.preventDefault();
    e.stopPropagation();
    if (!cropImgRef.current) return;
    const content = getImageContentBox(cropImgRef.current);
    const handleX = content.left + corners[idx].x * content.width;
    const handleY = content.top + corners[idx].y * content.height;
    dragOffsetRef.current = {
      x: e.clientX - handleX,
      y: e.clientY - handleY,
    };
    setDragIdx(idx);
  }

  useEffect(() => {
    if (mode !== "crop" || !active) return;
    const raf = window.requestAnimationFrame(updateImageBox);
    const resizeObserver = new ResizeObserver(() => {
      updateImageBox();
    });
    if (cropRef.current) resizeObserver.observe(cropRef.current);
    if (cropImgRef.current) resizeObserver.observe(cropImgRef.current);
    window.addEventListener("resize", updateImageBox);
    window.addEventListener("scroll", updateImageBox, true);
    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateImageBox);
      window.removeEventListener("scroll", updateImageBox, true);
    };
  }, [mode, active]);

  useEffect(() => {
    if (dragIdx == null) return;
    const move = (e: PointerEvent) => {
      setCornerFromPointer(e.clientX, e.clientY, dragIdx);
    };
    const up = () => {
      setDragIdx(null);
      dragOffsetRef.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [dragIdx]);

  function openView(page: ScanPage) {
    setActiveId(page.id);
    setCorners(page.corners);
    setFilter(page.filter);
    setMode("view");
  }

  function openEdit(page: ScanPage) {
    setActiveId(page.id);
    setCorners(page.corners);
    setFilter(page.filter);
    setMode("crop");
  }

  function reorder(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return;
    setPages((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }

  const confirmSave = useCallback(
    async (opts: { title: string; destination: SimpleDocSaveDestination }) => {
      if (saveBusy) return;
      if (!pages.some((p) => p.processedUrl || p.croppedUrl)) {
        setSaveError("حداقل یک صفحه برش‌خورده لازم است");
        return;
      }
      const title = opts.title.trim() || "اسکن بدون عنوان";
      const name = scanFileName(title);

      const persist = async (nevisignContractId?: string) => {
        const id = libraryId ?? newScanDocId();
        const saved = await upsertScannedDocument({
          id,
          title,
          pages,
          nevisignContractId,
        });
        setLibraryId(saved.id);
        return saved;
      };

      if (opts.destination === "phone") {
        setSaveBusy(true);
        setSaveError(null);
        try {
          const out = await buildScanPdfBytes(pages);
          await persist();
          await saveAndShareBytes(out, name, "application/pdf");
          setDocName(title);
          setSaveOpen(false);
          void refreshLibrary();
        } catch (err) {
          setSaveError(
            err instanceof Error ? err.message : "ذخیره روی موبایل ناموفق بود",
          );
        } finally {
          setSaveBusy(false);
        }
        return;
      }

      const runCloud = async () => {
        setSaveBusy(true);
        setSaveError(null);
        try {
          const out = await buildScanPdfBytes(pages);
          const copy = new Uint8Array(out.byteLength);
          copy.set(out);
          const file = new File([copy], name, { type: "application/pdf" });
          const contract = await createContractFromUpload(apiBase, file);
          await persist(contract.id);
          setDocName(title);
          setSaveOpen(false);
          void refreshLibrary();
          if (onOpenContract) {
            onOpenContract(contract.id, contract.title || title);
          } else {
            alert(`اسکن در نویساین ذخیره شد (شناسه: ${contract.id}).`);
          }
        } catch (err) {
          if (
            handleApiAuthError(err, () => {
              void confirmSave({ title, destination: "nevisign" });
            })
          ) {
            return;
          }
          setSaveError(err instanceof Error ? err.message : "ذخیره ناموفق بود");
        } finally {
          setSaveBusy(false);
        }
      };

      if (!isAuthenticated) {
        openLogin(() => {
          void runCloud();
        });
        return;
      }
      await runCloud();
    },
    [
      apiBase,
      isAuthenticated,
      libraryId,
      onOpenContract,
      openLogin,
      pages,
      refreshLibrary,
      saveBusy,
    ],
  );

  async function sharePdf() {
    if (!hasReadyPages) {
      setError("حداقل یک صفحه برش‌خورده لازم است");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await buildScanPdfBytes(pages);
      const name = scanFileName(docName);
      if (navigator.share) {
        const copy = new Uint8Array(out.byteLength);
        copy.set(out);
        const blob = new Blob([copy], { type: "application/pdf" });
        const file = new File([blob], name, { type: "application/pdf" });
        await navigator.share({ files: [file], title: docName });
      } else {
        await saveAndShareBytes(out, name, "application/pdf");
      }
    } catch (err) {
      if ((err as Error)?.name !== "AbortError") {
        setError(err instanceof Error ? err.message : "اشتراک‌گذاری ناموفق");
      }
    } finally {
      setBusy(false);
    }
  }

  const ordered = orderCorners(corners);
  const viewUrl =
    active?.processedUrl || active?.croppedUrl || active?.originalDataUrl;

  if (screen === "library") {
    return (
      <ToolPageShell
        embedded
        pageClassName="scan-page"
        toolId="scan-document"
        kicker="کتابخانه اسکن"
        title="اسناد اسکن‌شده من"
        description="آفلاین روی همین دستگاه؛ برای ویرایش، خروجی یا ذخیره دوباره باز کنید."
        heroSrc={null}
        onClose={() => {
          setScreen("editor");
          if (!pages.length) resetEditor();
        }}
        busy={libraryBusy}
        footer={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={libraryBusy}
              onClick={() => void refreshLibrary()}
            >
              {libraryBusy ? <Loader2 size={16} className="spin" /> : null}
              به‌روزرسانی
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                resetEditor();
                setScreen("editor");
              }}
            >
              <Camera size={16} />
              اسکن جدید
            </button>
          </>
        }
      >
        {libraryBusy && !library.length ? (
          <p className="scan-library-empty">در حال بارگذاری…</p>
        ) : null}
        {!libraryBusy && !library.length ? (
          <p className="scan-library-empty">
            هنوز سندی ذخیره نشده. بعد از اسکن، «ذخیره» را بزنید.
          </p>
        ) : null}

        <ul className="scan-library-list">
          {library.map((doc) => (
            <li key={doc.id} className="scan-library-item">
              <button
                type="button"
                className="scan-library-open"
                onClick={() => void loadDocument(doc.id)}
              >
                <span className="scan-library-thumb">
                  {doc.thumbnailUrl ? (
                    <img src={doc.thumbnailUrl} alt="" />
                  ) : (
                    <FolderOpen size={20} aria-hidden />
                  )}
                </span>
                <span className="scan-library-meta">
                  <strong>{doc.title}</strong>
                  <small>
                    {toPersianDigits(doc.pageCount)} صفحه ·{" "}
                    {formatScanDate(doc.updatedAt)}
                    {doc.nevisignContractId ? " · نویساین" : ""}
                  </small>
                </span>
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="حذف"
                onClick={() => {
                  if (!window.confirm(`«${doc.title}» حذف شود؟`)) return;
                  void removeScannedDocument(doc.id).then(() => {
                    if (libraryId === doc.id) resetEditor();
                    void refreshLibrary();
                  });
                }}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>

        {error ? <p className="reader-login-error">{error}</p> : null}
      </ToolPageShell>
    );
  }

  const captureFooter =
    mode === "crop" && active ? (
      <>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            if (active.croppedUrl) setMode("view");
            else {
              setPages((prev) => prev.filter((p) => p.id !== active.id));
              setActiveId(null);
              setMode("capture");
            }
          }}
        >
          انصراف
        </button>
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={() => void applyCrop()}
        >
          {busy ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
          تأیید برش
        </button>
      </>
    ) : mode === "view" && active ? (
      <>
        <button
          type="button"
          className="ghost-btn"
          disabled={busy}
          onClick={() => openEdit(active)}
        >
          <Pencil size={16} />
          ویرایش
        </button>
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={() => setMode("capture")}
        >
          <Check size={16} />
          تأیید صفحه
        </button>
      </>
    ) : mode === "capture" && !isEmptyCapture ? (
      <>
        <button
          type="button"
          className="ghost-btn"
          disabled={!hasReadyPages || busy || saveBusy}
          onClick={() => void sharePdf()}
        >
          <Share2 size={16} />
          اشتراک PDF
        </button>
        <button
          type="button"
          className="primary-btn"
          disabled={!hasReadyPages || busy || saveBusy}
          onClick={() => {
            setSaveError(null);
            setSaveOpen(true);
          }}
        >
          {busy || saveBusy ? (
            <Loader2 size={16} className="spin" />
          ) : (
            <Save size={16} />
          )}
          ذخیره
        </button>
      </>
    ) : null;

  const scanDescription =
    mode === "crop"
      ? "چهار گوشه را تنظیم کنید و برش را تأیید کنید."
      : mode === "view"
        ? "نتیجه را ببینید، فیلتر بزنید و صفحه را تأیید کنید."
        : "عکس بگیرید، چهار گوشه را تنظیم کنید و در پایان یک PDF تمیز و قابل اشتراک بسازید.";

  return (
    <ToolPageShell
      embedded
      pageClassName="scan-page"
      toolId="scan-document"
      kicker="اسکن موبایلی"
      title="اسکن سند"
      description={scanDescription}
      heroSrc={
        mode === "capture" && isEmptyCapture
          ? "/illustrations/scan-hero.svg"
          : null
      }
      onClose={onBack}
      busy={busy || saveBusy}
      footer={captureFooter}
    >
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void addFromFile(f);
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void addFromFile(f);
        }}
      />

      {mode === "crop" && active ? (
        <div className="scan-crop-wrap">
          <p className="scan-mode-hint">ویرایش برش — عکس اصلی با قاب چهارگوشه</p>
          <div className="scan-crop-stage" ref={cropRef}>
            <img
              ref={cropImgRef}
              src={active.originalDataUrl}
              alt=""
              draggable={false}
              onLoad={updateImageBox}
            />
            {imageBox && imageBox.width > 0 ? (
              <svg
                className="scan-quad"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                style={{
                  left: imageBox.left,
                  top: imageBox.top,
                  width: imageBox.width,
                  height: imageBox.height,
                }}
              >
                <polygon
                  points={ordered.map((c) => `${c.x},${c.y}`).join(" ")}
                  fill="color-mix(in srgb, var(--accent) 18%, transparent)"
                  stroke="var(--accent)"
                  strokeWidth="0.008"
                />
              </svg>
            ) : null}
            {corners.map((c, i) => (
              <button
                key={i}
                type="button"
                className="scan-corner"
                style={{
                  left: imageBox
                    ? imageBox.left + c.x * imageBox.width
                    : `${c.x * 100}%`,
                  top: imageBox
                    ? imageBox.top + c.y * imageBox.height
                    : `${c.y * 100}%`,
                }}
                aria-label={`گوشه ${i + 1}`}
                onPointerDown={(e) => beginDrag(e, i)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {mode === "view" && active && viewUrl ? (
        <div className="scan-view-wrap">
          <p className="scan-mode-hint">مشاهده — نتیجه برش‌خورده</p>
          <div className="scan-result-stage">
            <img src={viewUrl} alt="نتیجه اسکن" />
          </div>

          <div className="scan-filter-row" role="listbox" aria-label="فیلترها">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={filter === opt.id}
                className={`scan-filter-chip${filter === opt.id ? " is-active" : ""}`}
                disabled={busy || !active.croppedUrl}
                onClick={() => void changeFilter(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {mode === "capture" ? (
        <>
          {isEmptyCapture ? (
            <div className="scan-capture-only">
              <div className="scan-actions-row">
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy}
                  onClick={() => cameraRef.current?.click()}
                >
                  <Camera size={16} />
                  گرفتن عکس
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  disabled={busy}
                  onClick={() => galleryRef.current?.click()}
                >
                  <ImagePlus size={16} />
                  از گالری
                </button>
              </div>
              <button
                type="button"
                className="ghost-btn scan-library-entry"
                onClick={() => setScreen("library")}
              >
                <FolderOpen size={16} />
                اسناد اسکن‌شده من
              </button>
              <div className="dashboard-empty">
                <p>هنوز صفحه‌ای اسکن نشده است. از دوربین یا گالری شروع کنید.</p>
              </div>
            </div>
          ) : (
            <>
              <label htmlFor="scan-name">نام سند</label>
              <input
                id="scan-name"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
              />
              <div className="consumer-chip-row">
                <span className="consumer-chip">
                  صفحه‌ها: {toPersianDigits(pages.length)}
                </span>
                <span className="consumer-chip">خروجی نهایی: PDF</span>
                <span className="consumer-chip">مناسب اسناد چندصفحه‌ای</span>
              </div>

              <div className="scan-actions-row">
                <button
                  type="button"
                  className="primary-btn"
                  disabled={busy}
                  onClick={() => cameraRef.current?.click()}
                >
                  <Camera size={16} />
                  گرفتن عکس
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  disabled={busy}
                  onClick={() => galleryRef.current?.click()}
                >
                  <ImagePlus size={16} />
                  از گالری
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setScreen("library")}
                >
                  <FolderOpen size={16} />
                  کتابخانه
                </button>
              </div>

              <ul className="scan-gallery">
                {pages.map((p, idx) => (
                  <li key={p.id} className="scan-thumb">
                    <button
                      type="button"
                      className="scan-thumb-btn"
                      onClick={() =>
                        p.croppedUrl ? openView(p) : openEdit(p)
                      }
                    >
                      <img
                        src={
                          p.processedUrl ||
                          p.croppedUrl ||
                          p.originalDataUrl
                        }
                        alt={`صفحه ${idx + 1}`}
                      />
                      <span>{idx + 1}</span>
                    </button>
                    <div className="scan-thumb-tools">
                      <button
                        type="button"
                        aria-label="مشاهده"
                        disabled={!p.croppedUrl}
                        onClick={() => openView(p)}
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        type="button"
                        aria-label="ویرایش"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        disabled={idx === 0}
                        aria-label="بالا"
                        onClick={() => reorder(idx, idx - 1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        disabled={idx === pages.length - 1}
                        aria-label="پایین"
                        onClick={() => reorder(idx, idx + 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        aria-label="حذف"
                        onClick={() => {
                          setPages((prev) => {
                            const next = prev.filter((x) => x.id !== p.id);
                            if (!next.length) {
                              setActiveId(null);
                              setMode("capture");
                            }
                            return next;
                          });
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : null}

      {error ? <p className="reader-login-error">{error}</p> : null}

      <SimpleDocSaveModal
        open={saveOpen}
        initialTitle={docName}
        busy={saveBusy}
        error={saveError}
        dialogLabel="ذخیره اسکن"
        phoneLabel="ذخیره در موبایل"
        phoneHint="دانلود / اشتراک PDF روی دستگاه"
        nevisignLabel="ذخیره در نویساین"
        nevisignHint="آپلود PDF در حساب (در صورت نیاز ورود)"
        onClose={() => {
          if (saveBusy) return;
          setSaveOpen(false);
          setSaveError(null);
        }}
        onConfirm={(opts) => {
          void confirmSave(opts);
        }}
      />
    </ToolPageShell>
  );
}
